import { Link } from "react-router";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { getInitials } from "@/lib/format";

const AVATAR_SIZE = { xs: "h-5 w-5 text-[9px]", sm: "h-7 w-7 text-[10px]", md: "h-9 w-9 text-xs", lg: "h-12 w-12 text-sm" };

interface PersonAvatarProps {
  name: string | null | undefined;
  src?: string | null;
  size?: keyof typeof AVATAR_SIZE;
  className?: string;
}

export function PersonAvatar({ name, src, size = "sm", className }: PersonAvatarProps) {
  return (
    <Avatar className={cn(AVATAR_SIZE[size], className)}>
      {src && <AvatarImage src={src} alt="" />}
      <AvatarFallback className="bg-tone-accent-bg font-semibold text-tone-accent-fg">{getInitials(name)}</AvatarFallback>
    </Avatar>
  );
}

interface EntityRefProps {
  id: string;
  name: string | null | undefined;
  subtitle?: React.ReactNode;
  /** Algo à direita do nome (ex.: badge de disponibilidade). */
  trailing?: React.ReactNode;
  /** Sem link (ex.: dentro de um card que já é link). */
  plain?: boolean;
  className?: string;
}

function RefLayout({ to, plain, leading, name, subtitle, trailing, className }: {
  to: string;
  plain?: boolean;
  leading: React.ReactNode;
  name: string;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
  className?: string;
}) {
  const label = plain ? (
    <span className="truncate font-medium">{name}</span>
  ) : (
    <Link to={to} className="truncate font-medium hover:underline" onClick={(event) => event.stopPropagation()}>
      {name}
    </Link>
  );
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-2", className)}>
      {leading}
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="flex min-w-0 items-center gap-1.5">
          {label}
          {trailing}
        </span>
        {subtitle && <span className="truncate text-xs text-muted-foreground">{subtitle}</span>}
      </span>
    </span>
  );
}

/** Referência a um freelancer: avatar + nome com link real (cmd/ctrl-clique abre aba). */
export function FreelancerRef({ id, name, subtitle, trailing, plain, avatarUrl, size = "sm", className }: EntityRefProps & {
  avatarUrl?: string | null;
  size?: keyof typeof AVATAR_SIZE;
}) {
  return (
    <RefLayout
      to={`/freelancers/${id}`}
      plain={plain}
      leading={<PersonAvatar name={name} src={avatarUrl} size={size} />}
      name={name || "Sem nome"}
      subtitle={subtitle}
      trailing={trailing}
      className={className}
    />
  );
}

/** Referência a uma agência, com a cor e o logo que ela cadastrou. */
export function AgenciaRef({ id, name, subtitle, trailing, plain, cor, logoUrl, className }: EntityRefProps & {
  cor?: string | null;
  logoUrl?: string | null;
}) {
  const leading = logoUrl ? (
    <img src={logoUrl} alt="" className="h-5 w-5 shrink-0 rounded object-contain" />
  ) : (
    <span
      className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10"
      style={{ backgroundColor: cor || "hsl(var(--muted-foreground) / 0.4)" }}
    />
  );
  return (
    <RefLayout
      to={`/agencias/${id}`}
      plain={plain}
      leading={leading}
      name={name || "Sem nome"}
      subtitle={subtitle}
      trailing={trailing}
      className={className}
    />
  );
}

/** Usuário interno (responsável, autor): avatar pequeno + nome, sem link. */
export function UserRef({ name, className }: { name: string | null | undefined; className?: string }) {
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
      <PersonAvatar name={name} size="xs" />
      <span className="truncate text-sm">{name || "Sem responsável"}</span>
    </span>
  );
}
